import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Button, Card, Descriptions, Empty, Spin, Tag, Timeline, Typography} from 'antd';
import {ArrowLeftOutlined} from '@ant-design/icons';
import {executionService} from '../../services/executionService';
import {AgentExecutionRunDetail, AgentExecutionTurn} from '../../types/agent';
import dayjs from 'dayjs';

const {Title, Text} = Typography;

const getStatusColor = (status: string): string => {
    switch (status) {
        case 'COMPLETED':
            return 'green';
        case 'RUNNING':
            return 'blue';
        case 'FAILED':
            return 'red';
        case 'SUSPENDED':
            return 'orange';
        case 'INTERRUPTED':
            return 'volcano';
        default:
            return 'default';
    }
};

const getPhaseColor = (phase: string): string => {
    switch (phase) {
        case 'STARTING_GUN':
            return 'gold';
        case 'FC_LOOP':
            return 'blue';
        case 'OUTPUT_VALIDATION':
            return 'purple';
        case 'FINAL_OUTPUT':
            return 'green';
        default:
            return 'default';
    }
};

const ExecutionDetail: React.FC = () => {
    const {executionId} = useParams<{ executionId: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState<AgentExecutionRunDetail | null>(null);
    const [turns, setTurns] = useState<AgentExecutionTurn[]>([]);
    const [expandedTurn, setExpandedTurn] = useState<number | null>(null);

    useEffect(() => {
        if (!executionId) return;
        const loadData = async () => {
            setLoading(true);
            try {
                const [detailData, turnsData] = await Promise.all([
                    executionService.getAgentExecution(executionId),
                    executionService.getAgentExecutionTurns(executionId),
                ]);
                setDetail(detailData);
                setTurns(turnsData);
            } catch {
                // 静默处理加载错误
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [executionId]);

    if (loading) {
        return (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh'}}>
                <Spin size="large"/>
            </div>
        );
    }

    if (!detail) {
        return (
            <div>
                <Button icon={<ArrowLeftOutlined/>} onClick={() => navigate(-1)}
                        style={{marginBottom: 16}}>返回</Button>
                <Empty description="未找到执行记录"/>
            </div>
        );
    }

    return (
        <div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
                <Title level={2}>执行详情</Title>
                <Button icon={<ArrowLeftOutlined/>} onClick={() => navigate(-1)}>返回</Button>
            </div>

            {/* 顶部面板：触发信息 */}
            <Card title="触发信息" style={{marginBottom: 16}}>
                <Descriptions column={3} bordered size="small">
                    <Descriptions.Item label="执行ID">{detail.executionId}</Descriptions.Item>
                    <Descriptions.Item label="Trace ID">{detail.traceId}</Descriptions.Item>
                    <Descriptions.Item label="智能体">
                        {detail.agentName} (ID: {detail.agentId})
                    </Descriptions.Item>
                    <Descriptions.Item label="触发类型">
                        <Tag>{detail.triggerKind}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="触发码">{detail.triggerCode || '-'}</Descriptions.Item>
                    <Descriptions.Item label="触发ID">{detail.triggerId || '-'}</Descriptions.Item>
                    <Descriptions.Item label="触发来源">{detail.triggerSource || '-'}</Descriptions.Item>
                    <Descriptions.Item label="会话ID">{detail.sessionId || '-'}</Descriptions.Item>
                    <Descriptions.Item label="执行目的">{detail.executionPurpose || '-'}</Descriptions.Item>
                    <Descriptions.Item label="副作用模式">
                        <Tag
                            color={detail.sideEffectMode === 'LIVE' ? 'red' : detail.sideEffectMode === 'SAFE' ? 'orange' : 'blue'}>
                            {detail.sideEffectMode || '-'}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="恢复模式">
                        {detail.resumeMode ? <Tag color="purple">RESUME</Tag> : <Tag>NORMAL</Tag>}
                    </Descriptions.Item>
                    <Descriptions.Item label="恢复来源">
                        {detail.resumeSourceKind ? `${detail.resumeSourceKind}: ${detail.resumeSourceId}` : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="状态">
                        <Tag color={getStatusColor(detail.status)}>{detail.status}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Turn进度">
                        {detail.currentTurn} / {detail.totalTurns}
                    </Descriptions.Item>
                    <Descriptions.Item label="模型ID">{detail.modelId || '-'}</Descriptions.Item>
                    <Descriptions.Item label="开始时间">
                        {detail.startedAt ? dayjs(detail.startedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="结束时间">
                        {detail.finishedAt ? dayjs(detail.finishedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="创建时间">
                        {detail.createdAt ? dayjs(detail.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            {/* 中间面板：Turns时间线 */}
            <Card title={`Turns时间线 (${turns.length})`} style={{marginBottom: 16}}>
                {turns.length === 0 ? (
                    <Empty description="无Turn数据"/>
                ) : (
                    <Timeline
                        items={turns.map((turn) => ({
                            color: getPhaseColor(turn.phase),
                            dot: (
                                <div
                                    style={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: turn.errorMessage ? 'var(--pt-bear)' : 'var(--pt-primary)',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setExpandedTurn(expandedTurn === turn.turnNo ? null : turn.turnNo)}
                                />
                            ),
                            children: (
                                <div
                                    style={{cursor: 'pointer'}}
                                    onClick={() => setExpandedTurn(expandedTurn === turn.turnNo ? null : turn.turnNo)}
                                >
                                    <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                                        <Text strong>Turn {turn.turnNo}</Text>
                                        <Tag color={getPhaseColor(turn.phase)}>{turn.phase}</Tag>
                                        {turn.responseType && <Tag>{turn.responseType}</Tag>}
                                        {turn.functionName && <Tag color="cyan">{turn.functionName}</Tag>}
                                        {turn.outputKind && <Tag color="green">{turn.outputKind}</Tag>}
                                        {null != turn.durationMs && (
                                            <Text type="secondary">{turn.durationMs}ms</Text>
                                        )}
                                        {turn.errorMessage && <Tag color="red">ERROR</Tag>}
                                    </div>
                                    {expandedTurn === turn.turnNo && (
                                        <div style={{
                                            marginTop: 8,
                                            paddingLeft: 16,
                                            borderLeft: '2px solid rgba(255,255,255,0.13)'
                                        }}>
                                            <Descriptions column={2} size="small" bordered>
                                                <Descriptions.Item
                                                    label="模型ID">{turn.modelId || '-'}</Descriptions.Item>
                                                <Descriptions.Item
                                                    label="工具选择">{turn.toolChoice || '-'}</Descriptions.Item>
                                                <Descriptions.Item
                                                    label="工具调用ID">{turn.toolCallId || '-'}</Descriptions.Item>
                                                <Descriptions.Item
                                                    label="输出码">{turn.outputCode || '-'}</Descriptions.Item>
                                                <Descriptions.Item label="Token使用" span={2}>
                                                    {turn.tokenUsageJson || '-'}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="请求消息引用" span={2}>
                                                    <Text
                                                        style={{fontSize: 12, fontFamily: 'monospace'}}>
                                                        {turn.requestMessageRefsJson || '-'}
                                                    </Text>
                                                </Descriptions.Item>
                                                <Descriptions.Item label="追加消息引用" span={2}>
                                                    <Text
                                                        style={{fontSize: 12, fontFamily: 'monospace'}}>
                                                        {turn.appendedMessageRefsJson || '-'}
                                                    </Text>
                                                </Descriptions.Item>
                                                {turn.errorMessage && (
                                                    <Descriptions.Item label="错误信息" span={2}>
                                                        <Text type="danger">{turn.errorMessage}</Text>
                                                    </Descriptions.Item>
                                                )}
                                                <Descriptions.Item label="创建时间">
                                                    {turn.createdAt ? dayjs(turn.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                                                </Descriptions.Item>
                                            </Descriptions>
                                        </div>
                                    )}
                                </div>
                            ),
                        }))}
                    />
                )}
            </Card>

            {/* 底部面板：消息/输出 */}
            <Card title="消息/输出">
                <Descriptions column={2} bordered size="small">
                    <Descriptions.Item label="最终输出类型">
                        {detail.finalOutputKind ? <Tag color="green">{detail.finalOutputKind}</Tag> : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="最终输出码">{detail.finalOutputCode || '-'}</Descriptions.Item>
                    <Descriptions.Item label="最终输出引用ID">{detail.finalOutputRefId || '-'}</Descriptions.Item>
                    <Descriptions.Item label="挂起原因">{detail.suspendReason || '-'}</Descriptions.Item>
                    {detail.errorMessage && (
                        <Descriptions.Item label="错误信息" span={2}>
                            <Text type="danger">{detail.errorMessage}</Text>
                        </Descriptions.Item>
                    )}
                </Descriptions>
            </Card>
        </div>
    );
};

export default ExecutionDetail;